import { useImagesWarmFastCache } from "@design-lab/entities/artwork/model/useImagesFastCache";
import {
  trackAiImageRefinementRedo,
  trackAiImageRefinementUndo
} from "@design-lab/shared/lib/analytics";
import { useNotification } from "@design-lab/shared/ui/notification";
import { useRefineAiImage } from "../api";
import { type AiDesignState } from "./state";
import { useRefinementHistory } from "./useRefinementHistory";

type UseAiImageRefinementOptions = {
  side: "front" | "back";
  currentState: AiDesignState[keyof AiDesignState];
  updateState: (updates: Partial<AiDesignState[keyof AiDesignState]>) => void;
};

export function useAiImageRefinement({
  side,
  currentState,
  updateState
}: UseAiImageRefinementOptions) {
  const refineImageMutation = useRefineAiImage();
  const { error: notifyError } = useNotification();
  const { addToHistory, undo, redo, canUndo, canRedo, clearHistory } = useRefinementHistory(side);
  const { warmCache } = useImagesWarmFastCache();

  const handleEditImage = (imageId: number) => {
    const selectedImage = currentState.images.find(img => img.id === imageId);
    if (!selectedImage) {
      notifyError({
        title: "Image not found",
        description: "Unable to find the selected image for refinement."
      });
      return;
    }

    // Initialize history with the current image
    addToHistory({
      imageId: selectedImage.id,
      imageSrc: selectedImage.imageSrc,
      prompt: ""
    });

    // Pre-cache the initial image for instant undo/redo
    warmCache([selectedImage.imageSrc]);

    // Enter refinement mode
    updateState({
      isRefining: true,
      refinementImageId: imageId
    });
  };

  const handleCancelRefinement = () => {
    // Clear history
    clearHistory();

    // Exit refinement mode
    updateState({
      isRefining: false,
      refinementImageId: undefined,
      refinementPrompt: "",
      refinementError: undefined
    });
  };

  const handleRefinementPromptChange = (prompt: string) => {
    updateState({ refinementPrompt: prompt });
  };

  const handleRefineSubmit = async (refinementPrompt: string) => {
    if (!currentState.refinementImageId) {
      notifyError({
        title: "Refinement Error",
        description: "No image selected for refinement."
      });
      return;
    }

    const selectedImage = currentState.images.find(
      img => img.id === currentState.refinementImageId
    );
    if (!selectedImage) {
      notifyError({
        title: "Image not found",
        description: "Unable to find the selected image for refinement."
      });
      return;
    }

    // Update state to show loading
    updateState({
      isGenerating: true,
      refinementError: undefined
    });

    try {
      // Call refine API
      const result = await refineImageMutation.mutateAsync({
        refinementPrompt,
        inputImageUrl: selectedImage.imageSrc,
        aiImageVersionId: String(selectedImage.id)
      });

      // Pre-cache the new image immediately for instant undo/redo
      warmCache([result.url]);

      // Add to history
      addToHistory({
        imageId: result.aiImageVersionId,
        imageSrc: result.url,
        prompt: refinementPrompt
      });

      // Update image in gallery with refined version
      const updatedImages = currentState.images.map(img =>
        img.id === currentState.refinementImageId
          ? {
              ...img,
              imageSrc: result.url,
              id: result.aiImageVersionId
            }
          : img
      );

      // Update state: keep in refinement mode with new image
      updateState({
        images: updatedImages,
        isGenerating: false,
        refinementImageId: result.aiImageVersionId
      });
    } catch (error: any) {
      console.error("Failed to refine image:", error);
      window.Honeybadger?.notify("Failed to refine image", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useAiImageRefinement",
          imageId: selectedImage.id
        }
      });

      const errorMessage = error?.response?.error || "Failed to refine image. Please try again.";

      updateState({
        isGenerating: false,
        refinementError: errorMessage
      });

      notifyError({
        title: "Refinement Failed",
        description: errorMessage
      });
    }
  };

  const handleUndo = () => {
    const previousEntry = undo();
    if (previousEntry) {
      trackAiImageRefinementUndo(previousEntry.imageId);

      const updatedImages = currentState.images.map(img =>
        img.id === currentState.refinementImageId
          ? {
              ...img,
              imageSrc: previousEntry.imageSrc,
              id: previousEntry.imageId
            }
          : img
      );

      updateState({
        images: updatedImages,
        refinementImageId: previousEntry.imageId,
        refinementPrompt: previousEntry.prompt
      });
    }
  };

  const handleRedo = () => {
    const nextEntry = redo();
    if (nextEntry) {
      trackAiImageRefinementRedo(nextEntry.imageId);

      const updatedImages = currentState.images.map(img =>
        img.id === currentState.refinementImageId
          ? {
              ...img,
              imageSrc: nextEntry.imageSrc,
              id: nextEntry.imageId
            }
          : img
      );

      updateState({
        images: updatedImages,
        refinementImageId: nextEntry.imageId,
        refinementPrompt: nextEntry.prompt
      });
    }
  };

  return {
    handleEditImage,
    handleCancelRefinement,
    handleRefinementPromptChange,
    handleRefineSubmit,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo
  };
}
